import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { roomService } from '../../../services/api';

const RoomFilters = ({ 
  searchTerm, 
  onSearchChange, 
  selectedType, 
  onTypeChange, 
  selectedStatus, 
  onStatusChange,
  selectedAmenities,
  onAmenitiesChange,
  onClearFilters 
}) => {
  const [roomTypes, setRoomTypes] = useState([
    { value: '', label: 'Todos os tipos' }
  ]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // Carregar tipos de quarto da API
  useEffect(() => {
    const loadRoomTypes = async () => {
      try {
        setLoadingTypes(true);
        const response = await roomService.getRoomTypes();
        
        // A API retorna { types: [...], enum: [...] }
        const typesArray = response.types || response;
        
        // Se já estiver no formato correto, usar diretamente
        const typesOptions = Array.isArray(typesArray) 
          ? typesArray.map(type => ({
              value: type.value || type.name,
              label: type.label || type.name
            }))
          : [];
        
        setRoomTypes([
          { value: '', label: 'Todos os tipos' },
          ...typesOptions
        ]);
      } catch (error) {
        console.error('Erro ao carregar tipos de quarto:', error);
        // Em caso de erro, manter os tipos padrão como fallback
        setRoomTypes([
          { value: '', label: 'Todos os tipos' },
          { value: 'STANDARD', label: 'Standard' },
          { value: 'DELUXE', label: 'Deluxe' },
          { value: 'SUITE', label: 'Suíte' },
          { value: 'PRESIDENTIAL', label: 'Presidencial' }
        ]);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadRoomTypes();
  }, []);

  const statusOptions = [
    { value: '', label: 'Todos os status' },
    { value: 'Disponível', label: 'Disponível' },
    { value: 'Ocupado', label: 'Ocupado' },
    { value: 'Manutenção', label: 'Manutenção' },
    { value: 'Limpeza', label: 'Limpeza' }
  ];

  const amenityOptions = [
    { key: 'hasAC', label: 'Ar Condicionado', icon: 'Wind' },
    { key: 'hasMinibar', label: 'Frigobar', icon: 'Coffee' },
    { key: 'hasBathroom', label: 'Banheiro Privativo', icon: 'Bath' }
  ];

  const hasActiveFilters = searchTerm || selectedType || selectedStatus || Object.values(selectedAmenities).some(Boolean);

  return (
    <div className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>
      
      <div className="relative space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Icon name="Filter" size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Filtros Avançados</h3>
              <p className="text-sm text-muted-foreground">Refine sua busca de quartos</p>
            </div>
          </div>
          
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-destructive/10 text-destructive font-medium rounded-lg border border-destructive/20 hover:bg-destructive/20 transition-all"
            >
              <Icon name="X" size={16} />
              <span className="text-sm">Limpar</span>
            </button>
          )}
        </div>

        {/* Search */}
        <div className="relative">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Buscar por número do quarto ou tipo..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 hover:border-primary/30 transition-all duration-200"
          />
        </div>

        {/* Filters Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Room Type Filter */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Icon name="Home" size={14} />
              Tipo de Quarto
              {loadingTypes && (
                <span className="text-xs text-muted-foreground">(carregando...)</span>
              )}
            </label>
            <select
              value={selectedType}
              onChange={(e) => onTypeChange(e.target.value)}
              disabled={loadingTypes}
              className="w-full px-3 py-2.5 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {roomTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Icon name="Activity" size={14} />
              Status
            </label>
            <select
              value={selectedStatus}
              onChange={(e) => onStatusChange(e.target.value)}
              className="w-full px-3 py-2.5 border border-border rounded-lg bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-primary hover:border-primary/30 transition-all duration-200"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Amenities Filter */}
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <Icon name="Star" size={14} />
              Comodidades
            </label>
            <div className="space-y-2 bg-muted/20 rounded-lg p-3 border border-border/50">
              {amenityOptions.map((amenity) => (
                <label key={amenity.key} className="flex items-center gap-3 cursor-pointer group p-2 rounded-lg hover:bg-primary/5 transition-all duration-200">
                  <input
                    type="checkbox"
                    checked={selectedAmenities[amenity.key] || false}
                    onChange={(e) => onAmenitiesChange(amenity.key, e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary w-4 h-4"
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Icon 
                      name={amenity.icon} 
                      size={16} 
                      className={`transition-colors duration-200 ${
                        selectedAmenities[amenity.key] 
                          ? 'text-primary' 
                          : 'text-muted-foreground group-hover:text-primary'
                      }`} 
                    />
                    <span className={`text-sm font-medium transition-colors duration-200 ${
                      selectedAmenities[amenity.key] 
                        ? 'text-primary' 
                        : 'text-foreground group-hover:text-primary'
                    }`}>
                      {amenity.label}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RoomFilters;